import React from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Container, Box } from '@mantine/core';
import PlatformNavBar from '../../components/PlatformNavBar';

import LandingPage from "./LandingPage";
import PlanLibrary from "./components/PlanLibrary";
import PlanEditor from "./components/PlanEditor";
import RunTrigger from "./components/RunTrigger";
import RunHistory from "./components/RunHistory";
import ReportViewer from "./components/ReportViewer";
import AllowlistManager from "./components/AllowlistManager";
import DiagramImporter from "./components/DiagramImporter";
import ExcelImporter from "./components/ExcelImporter";
import KbImporter from "./components/KbImporter";
import VerifyPlansScreen from "./components/VerifyPlansScreen";
import BatchDetail from "./components/BatchDetail";
import TestTemplates from "./components/TestTemplates";

import Footer from '../../components/Footer';

// Feature-flag for KB-driven entry points (SIT KB importer + AgentStudio
// "From KB" tab). Sourced from `REACT_APP_ENABLE_KB_FEATURES` injected by the
// SPA build via SSM lookup `/presidio-cc-tools/{env}/enable-kb-features`.
// Default false so any env that hasn't opted in stays safe.
const KB_FEATURES_ENABLED = process.env.REACT_APP_ENABLE_KB_FEATURES === 'true';

function SitApp({ onBackToPlatformSelector, platformInfo }) {
  const navigate = useNavigate();
  const location = useLocation();

  const tools = {
    plans: {
      name: "Test Plan Library",
      description: "Browse, author, and version YAML test plans for IVR / DTMF / AI-agent flows.",
      route: "/plans",
      component: PlanLibrary
    },
    run: {
      name: "Run a Test",
      description: "Pick a plan, choose an allowlisted destination, and place a real outbound test call.",
      route: "/run",
      component: RunTrigger
    },
    history: {
      name: "Run History",
      description: "Browse past runs, filter by plan or status, and open detailed reports.",
      route: "/history",
      component: RunHistory
    },
    allowlist: {
      name: "Destination Allowlist",
      description: "Manage the DIDs the harness is permitted to dial, with consent basis and expiry.",
      route: "/allowlist",
      component: AllowlistManager
    },
    diagrams: {
      name: "Diagram Importer",
      description: "Drop one or more IVR call-flow diagrams (PNG/JPG/etc.) and Bedrock vision drafts a test plan per branch.",
      route: "/diagrams",
      component: DiagramImporter
    },
    excel: {
      name: "Excel Test-Script Importer",
      description: "Upload an .xlsx test-script (per-step rows grouped by Test No#) and Bedrock synthesizes one SIT plan per test case.",
      route: "/excel",
      component: ExcelImporter
    },
    // KB-driven test generation — only surfaced when the env opts in.
    ...(KB_FEATURES_ENABLED ? {
      kb: {
        name: "Conversational AI-Agent Tests (KB)",
        description: "Upload a knowledge base (PDF / Markdown / HTML / TXT) and Bedrock synthesizes positive, negative, and persona test plans for an autonomous AI agent grounded in that KB.",
        route: "/kb",
        component: KbImporter
      }
    } : {}),
    templates: {
      name: "Test Plan Templates",
      description: "Ready-to-run sample plans for common scenarios — smoke tests, touch-tone menus, queue transfers, IVA conversations.",
      route: "/templates",
      component: TestTemplates
    }
  };

  const handleToolSelect = (key) => {
    navigate(tools[key].route);
  };

  const handleBackToLanding = () => {
    navigate('/');
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Container size="xl" style={{ flex: 1, padding: '20px' }}>
        {/* Top-left navigation: exit to platform selector + return to SIT home */}
        <PlatformNavBar
          onBackToPlatformSelector={onBackToPlatformSelector}
          onBackToHome={() => navigate('/')}
          showHome={location.pathname !== '/'}
          homeLabel="SIT home"
          color="violet"
          mb="xl"
        />

        <Routes>
          <Route
            path="/"
            element={
              <LandingPage
                tools={tools}
                onSelect={handleToolSelect}
                platformInfo={platformInfo}
              />
            }
          />
          <Route
            path="/plans"
            element={<PlanLibrary onBack={handleBackToLanding} />}
          />
          <Route
            path="/plans/:planId/edit"
            element={<PlanEditor onBack={() => navigate('/plans')} />}
          />
          <Route
            path="/plans/new"
            element={<PlanEditor onBack={() => navigate('/plans')} />}
          />
          <Route
            path="/run"
            element={<RunTrigger onBack={handleBackToLanding} />}
          />
          <Route
            path="/history"
            element={<RunHistory onBack={handleBackToLanding} />}
          />
          <Route
            path="/history/batches/:batchId"
            element={<BatchDetail onBack={() => navigate('/history')} />}
          />
          <Route
            path="/history/:runId"
            element={<ReportViewer onBack={() => navigate('/history')} />}
          />
          <Route
            path="/allowlist"
            element={<AllowlistManager onBack={handleBackToLanding} />}
          />
          <Route
            path="/diagrams"
            element={<DiagramImporter onBack={handleBackToLanding} />}
          />
          <Route
            path="/excel"
            element={<ExcelImporter onBack={handleBackToLanding} />}
          />
          {KB_FEATURES_ENABLED && (
            <Route
              path="/kb"
              element={<KbImporter onBack={handleBackToLanding} />}
            />
          )}
          <Route
            path="/verify"
            element={<VerifyPlansScreen onBack={handleBackToLanding} />}
          />
          <Route
            path="/templates"
            element={<TestTemplates onBack={handleBackToLanding} />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Container>

      <Footer platformColor="violet" />
    </Box>
  );
}

export default SitApp;
